import { Clock, MapPin } from 'lucide-react';

import { MediaFrame, Parallax, PointerParallax, ScrollReveal, SplitReveal } from '@/components/motion';
import { CAMPUS_ADDRESS, CAMPUS_FACILITIES, CAMPUS_HOURS, CAMPUS_NOTES } from '@/data/experience';

/** Real exterior photo of the Thalwil campus building (image only, no video). */
const CAMPUS_IMAGE = '/media/images/campus/ibas-thalwil-building-exterior.webp';

/**
 * CampusThalwil (/experience — section 1) — "A home base just outside Zurich".
 *
 * A calm, editorial opener for the physical campus: a two-column top pairs a
 * short lede, the real Thalwil address (MapPin) and the two labelled opening-hour
 * rows on the left with a scroll/pointer-parallax campus photo on the right.
 * Below, the five CAMPUS_FACILITIES render as ScrollReveal-staggered plain
 * hover-lift cards (icon in a soft navy chip), then the CAMPUS_NOTES render as a
 * two-column list of small practical rows. Every fact comes from CAMPUS_* in
 * '@/data/experience'; nothing about the campus is invented.
 *
 * Motion: SplitReveal headline, ScrollReveal (child-stagger) for the facility
 * grid and the notes, Parallax + PointerParallax depth on the media plate. All
 * primitives are reduced-motion safe and never strand content hidden.
 */
export function CampusThalwil() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Two-column top: copy + address + hours · campus photo ────────── */}
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          {/* ── LEFT: eyebrow, headline, lede, address, hours ──────────────── */}
          <div className="max-w-xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              IBAS Campus · Thalwil
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              A home base just outside Zurich.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                Our on-site floor in Thalwil occupies an entire building level — a calm,
                well-connected base for lectures, collaboration and the moments in between.
              </p>
            </ScrollReveal>

            {/* ── Address ──────────────────────────────────────────────────── */}
            <ScrollReveal from="up" delay={0.15}>
              <div className="mt-8 flex items-start gap-4 rounded-xl border border-border bg-background p-5">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                  <MapPin aria-hidden className="size-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                    Address
                  </p>
                  <p className="mt-1 font-display text-lg leading-tight font-semibold text-foreground">
                    {CAMPUS_ADDRESS}
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* ── Opening hours ────────────────────────────────────────────── */}
            <ScrollReveal stagger={0.08} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { label: 'Office hours', value: CAMPUS_HOURS.office },
                { label: 'Campus hours', value: CAMPUS_HOURS.campus },
              ].map((row) => (
                <div key={row.label} className="rounded-xl border border-border bg-muted/30 p-5">
                  <div className="flex items-center gap-2 text-brand-navy">
                    <Clock aria-hidden className="size-4" />
                    <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                      {row.label}
                    </p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground">{row.value}</p>
                </div>
              ))}
            </ScrollReveal>
          </div>

          {/* ── RIGHT: campus photo with depth ─────────────────────────────── */}
          <Parallax speed={0.12} className="mx-auto w-full max-w-md lg:max-w-none">
            <PointerParallax strength={16}>
              <div data-parallax data-depth="0.7">
                <MediaFrame
                  image={CAMPUS_IMAGE}
                  alt="Exterior view of the copper-panelled IBAS Thalwil campus building."
                  aspect="aspect-[4/5]"
                  overlay={30}
                  rounded="rounded-2xl"
                  position="50% 40%"
                />
              </div>
            </PointerParallax>
          </Parallax>
        </div>

        {/* ── Facilities grid — the five on-site spaces ────────────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            On the floor
          </p>
          <ScrollReveal
            stagger={0.1}
            className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {CAMPUS_FACILITIES.map((facility) => (
              <div
                key={facility.name}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                  <facility.Icon aria-hidden className="size-6" />
                </div>
                <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                  {facility.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {facility.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>

        {/* ── Practical notes — getting here & living nearby ───────────────── */}
        <div className="mt-16 border-t border-border pt-12">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Getting here &amp; nearby
          </p>
          <ScrollReveal stagger={0.08} className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {CAMPUS_NOTES.map((note) => (
              <div key={note.label} className="flex items-start gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                  <note.Icon aria-hidden className="size-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                    {note.label}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {note.value}
                  </p>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
